import { ft } from '../core/units.js';
import { parseTime } from './clock.js';
import { weekdayOf } from './npc.js';
import { DINNERS } from '../data/dinners.js';

/**
 * A dinner outdoors, running: who is still sat at it, and how it ended.
 *
 * Nothing here moves an animal and nothing here decides what a guest saw. The
 * herd is the spawner's and goes where food goes; what a guest saw is
 * perception's, and what they tell Management about it — and whether it lands
 * on you — is the witnesses' and attribution's. This file does two things with
 * the answers, and they are the only two new things:
 *
 *   the tables are laid, and cleared when the evening is over
 *   a guest who has seen a herd, with the herd close, gets up and goes
 *   a dinner with too few left at it is over, and says so in its record
 *
 * And when somebody asked for it to be over (a beat that `posts` it), ending
 * early pays them what the row says, through standing like any other award.
 */

/** Game minutes. A sighting this recent is somebody looking at it now. */
const SEEN_WITHIN = 1;

/*
 * Real seconds of a herd at the next table before a guest is out of the chair.
 *
 * Not the first frame. Somebody who looks up and sees javelina six yards off
 * stares at them for a moment first — which is also the moment in which they
 * get sure enough to say something to somebody, through the ordinary path.
 * Leaving on the first frame took every one of them out of sight of it before
 * they had decided anything, and a dinner emptied by a herd with not one
 * complaint about the herd is not what a herd at a dinner is.
 */
const STARE = 5;

const clockOf = (clock) => clock.day * 1440 + clock.minutes;

export function createDinners({ npcs, perception, wildlife, clock, standing, hud, entities = null, propAt = null, rows = DINNERS }) {
  /**
   * Dinners somebody has asked to see ended: the id, and the one night the
   * asking is about. Null until that night starts — the next time the dinner
   * runs after the beat, or tonight's if it is running as it is played. When
   * that night is over, however it ended, the asking is spent. A herd that
   * ends a later Friday on its own ends it for nobody.
   */
  let wanted = new Map();
  /** One per dinner per night it ran: who sat down, who left, how it ended. */
  let records = [];
  /** How long each guest has had a herd close and in sight. Real seconds. */
  const staring = new Map();

  const guestsOf = (row) => npcs.filter((npc) => npc.data.dinner === row.id);

  function running(row) {
    if (!row.days.includes(weekdayOf(clock.day))) return false;
    return clock.minutes >= parseTime(row.from) && clock.minutes < parseTime(row.to);
  }

  function recordFor(row, day) {
    return records.find((record) => record.id === row.id && record.day === day) ?? null;
  }

  /** Out past the tables, then the row's way off the lawn. Metres. */
  function exitFrom(row, npc) {
    const [first] = row.exit;
    return [
      { x: ft(first[0]), z: npc.position.z },
      ...row.exit.map(([x, z]) => ({ x: ft(x), z: ft(z) })),
    ];
  }

  /**
   * Whether this guest has seen a herd and has it close now.
   *
   * Seen is their own perception record of it, refreshed this minute; close
   * is where the animal is this frame. A herd across the lawn they saw ten
   * minutes ago and a herd at the next table are both on their record, and
   * only one of them ends dinner.
   */
  function scaredOff(npc, close) {
    const now = clockOf(clock);
    for (const record of perception.by(npc.data.id)) {
      if (record.what !== 'wildlife' || record.mode !== 'saw' || !record.subject) continue;
      // Seeing it now, not having seen it: a herd they watched go by an hour
      // ago and is now behind the lot fence is not at their table.
      if (now - record.at > SEEN_WITHIN) continue;
      const creature = wildlife.creatures.find((entry) => entry.id === record.subject);
      if (!creature) continue;
      if (Math.hypot(creature.position.x - npc.position.x, creature.position.z - npc.position.z) <= close) {
        return creature;
      }
    }
    return null;
  }

  /**
   * The tables, laid: the row's food down the middle of each. That is all it
   * is. What food on a lawn at dusk does is the desert's business.
   */
  function lay(row) {
    if (!entities || !propAt || !row.serves) return;
    for (const id of row.tables ?? []) {
      const table = propAt(id);
      if (!table) continue;
      const long = table.halfX >= table.halfZ;
      const half = long ? table.halfX : table.halfZ;
      for (let i = 0; i < row.serves.each; i += 1) {
        const along = -half + ((i + 0.5) * 2 * half) / row.serves.each;
        entities.spawn({
          kind: row.serves.kind,
          position: {
            x: table.x + (long ? along : 0),
            y: ft(row.serves.height ?? 0),
            z: table.z + (long ? 0 : along),
          },
          data: { dinner: row.id },
        });
      }
    }
  }

  /** And cleared, when the evening is over. Whatever is left of it. */
  function clearTables(row) {
    if (!entities) return;
    for (const entity of [...entities.all]) {
      if (entity.data?.dinner === row.id) entities.remove(entity.id);
    }
  }

  function end(row, record, guests) {
    record.outcome = 'ended-early';
    record.endedAt = clock.minutes;
    // The rest of the table, who were not going to sit on without the others.
    for (const npc of guests) {
      if (!npc.present || npc.leaving) continue;
      npc.say('I think that is us.', 3);
      npc.leave(exitFrom(row, npc), { day: clock.day });
    }
    hud?.say(`${row.title} has broken up early.`, { tone: 'info', seconds: 5 });
    if (wanted.get(row.id) === record.day) {
      record.forWhom = true;
      standing?.applyAll(row.award ?? {}, { reason: 'Word got back to the pass about the lawn dinner' });
    }
    spend(row, record);
  }

  /** The night somebody asked about is over, one way or the other. */
  function spend(row, record) {
    if (wanted.get(row.id) === record.day) wanted.delete(row.id);
  }

  return {
    rows,

    /** Somebody wants this one over. From a beat, when it is played. */
    post(id) {
      const row = rows.find((entry) => entry.id === id);
      if (!row) return;
      const tonight = running(row) ? recordFor(row, clock.day) : null;
      wanted.set(id, tonight && !tonight.outcome ? tonight.day : null);
    },

    get wanted() {
      return [...wanted.keys()];
    },

    get records() {
      return records.map((record) => ({ ...record, left: [...record.left] }));
    },

    /** The dinners on tonight, and who is still sat at each. */
    running() {
      return rows.filter(running).map((row) => {
        const record = recordFor(row, clock.day);
        const guests = guestsOf(row);
        return {
          id: row.id,
          title: row.title,
          seated: guests.filter((npc) => npc.present && !npc.leaving).length,
          covers: guests.length,
          outcome: record?.outcome ?? null,
        };
      });
    },

    update(dt = 1 / 60) {
      for (const row of rows) {
        const record = recordFor(row, clock.day);
        if (!running(row)) {
          // It ran its hours out. Recorded once, after the fact.
          if (record && !record.outcome && clock.minutes >= parseTime(row.to)) {
            record.outcome = 'ran';
            record.endedAt = parseTime(row.to);
            spend(row, record);
          }
          if (record && !record.cleared && clock.minutes >= parseTime(row.to)) {
            record.cleared = true;
            clearTables(row);
          }
          continue;
        }
        const guests = guestsOf(row);
        if (!record) {
          if (!guests.some((npc) => npc.present)) continue;
          records.push({ id: row.id, day: clock.day, covers: guests.length, left: [], outcome: null, endedAt: null });
          // Tonight is the night anybody who asked, and has not had one yet, asked about.
          if (wanted.has(row.id) && wanted.get(row.id) === null) wanted.set(row.id, clock.day);
          lay(row);
          continue;
        }
        if (record.outcome) continue;

        const close = ft(row.close);
        for (const npc of guests) {
          if (!npc.present || npc.leaving) continue;
          const creature = scaredOff(npc, close);
          if (!creature) {
            staring.delete(npc.data.id);
            continue;
          }
          const stared = (staring.get(npc.data.id) ?? 0) + dt;
          staring.set(npc.data.id, stared);
          if (stared < STARE) continue;
          staring.delete(npc.data.id);
          npc.say('No. No, we are going.', 3);
          npc.leave(exitFrom(row, npc), { day: clock.day });
          record.left.push({ id: npc.data.id, at: clock.minutes, because: creature.what });
        }

        const seated = guests.filter((npc) => npc.present && !npc.leaving).length;
        if (record.left.length && seated < record.covers * row.breaksUp) end(row, record, guests);
      }
    },

    toJSON: () => ({ wanted: [...wanted], records }),
    load(state) {
      // Older saves kept only the ids; each of those is still waiting for its night.
      wanted = new Map((state?.wanted ?? []).map((entry) => (Array.isArray(entry) ? entry : [entry, null])));
      records = (state?.records ?? []).map((record) => ({ ...record, left: [...(record.left ?? [])] }));
    },
    clear() {
      wanted = new Map();
      records = [];
      staring.clear();
    },
  };
}
